import { useState, useEffect, useRef, useCallback } from 'react'
import {
  IoSearchOutline,
  IoPersonOutline,
  IoSend,
  IoCheckmarkDone,
  IoCheckmark,
  IoCreateOutline,
  IoCloseOutline,
  IoTrashOutline,
  IoEllipsisVertical,
  IoAdd,
  IoChatbubbleEllipsesOutline,
  IoAttachOutline,
  IoDocumentOutline,
  IoDownloadOutline,
  IoOpenOutline,
} from 'react-icons/io5'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import {
  useConversations,
  useConversation,
  useCreateMessage,
  useUnreadCount,
  useUpdateMessage,
  useDeleteMessage,
  useMarkConversationAsRead,
  msgKeys,
} from '../hooks/useMessages'
import { useNurses } from '../hooks/useNurses'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { websocketClient } from '../services/websocket'
import { filesService } from '../services/api/files'
import type { Message } from '../types'
import { useAuthStore } from '../stores'
import { useQueryClient } from '@tanstack/react-query'

const getInitials = (name: string) => {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const formatMessageTime = (date: string) =>
  new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

const formatConvTime = (date: string) => {
  const d = new Date(date)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  if (isToday) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

const FILE_ID_RE = /^(?:file:)?(\d+)$/

function parseStoredFileId(url?: string): number | null {
  if (!url) return null
  const match = url.match(FILE_ID_RE)
  return match ? Number(match[1]) : null
}

function filenameFromContent(content: string): string | null {
  const match = content.match(/\[Fichier:\s*([^\]]+)\]/)
  return match?.[1]?.trim() || null
}

function textWithoutFileMarker(content: string): string {
  return content.replace(/\n?\[Fichier:\s*[^\]]+\]/g, '').trim()
}

const PREVIEWABLE_MIME = /^(image\/|application\/pdf)/

async function openOrDownloadAttachment(
  url: string,
  filename: string,
  mimeType: string | undefined,
  mode: 'open' | 'download'
) {
  if (url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  const fileId = parseStoredFileId(url)
  if (!fileId) throw new Error('Pièce jointe introuvable')

  let name = filename
  let mime = mimeType
  try {
    const meta = await filesService.getById(fileId)
    if (meta.data?.name) name = meta.data.name
    if (meta.data?.mimeType) mime = meta.data.mimeType
  } catch {
    /* on continue avec le nom du message */
  }

  const blob = await filesService.download(fileId)
  const objectUrl = URL.createObjectURL(blob)
  const canPreview = PREVIEWABLE_MIME.test(mime ?? blob.type ?? '')

  if (mode === 'open' && canPreview) {
    window.open(objectUrl, '_blank', 'noopener,noreferrer')
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    return
  }

  const link = document.createElement('a')
  link.href = objectUrl
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(objectUrl)
}

export default function Messages() {
  const user = useAuthStore(state => state.user)
  const currentUserId = user?.id ?? 0
  const currentUserIdStr = String(currentUserId)

  const queryClient = useQueryClient()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null)
  const selectedParticipantIdStr = selectedParticipantId ? String(selectedParticipantId) : ''
  const [messageInput, setMessageInput] = useState('')
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [messageMenuOpen, setMessageMenuOpen] = useState<number | null>(null)
  const [isNewConvOpen, setIsNewConvOpen] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [pendingAttachment, setPendingAttachment] = useState<{
    name: string
    fileId: number
    mimeType?: string
  } | null>(null)
  // Conversación ID activa (para WebSocket room)
  const activeConversationIdRef = useRef<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    data: conversations,
    isLoading: conversationsLoading,
  } = useConversations(currentUserIdStr)

  const { data: unreadCount } = useUnreadCount(currentUserIdStr)
  const { data: nursesList = [], isLoading: nursesLoading } = useNurses()

  const createMessage = useCreateMessage(currentUserIdStr, selectedParticipantIdStr)
  const updateMessage = useUpdateMessage(currentUserIdStr, selectedParticipantIdStr)
  const deleteMessage = useDeleteMessage(currentUserIdStr, selectedParticipantIdStr)
  const markConversationAsRead = useMarkConversationAsRead()

  const {
    data: messages = [],
    isLoading: conversationLoading,
  } = useConversation(currentUserIdStr, selectedParticipantIdStr)

  const selectedConv = conversations?.find(c => c.participant.id === selectedParticipantId)
  const selectedConvName = selectedConv
    ? `${selectedConv.participant.firstName} ${selectedConv.participant.lastName}`
    : ''

  // Auto-scroll en bas quand les messages changent
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  // Focus input quand conversation sélectionnée
  useEffect(() => {
    if (selectedParticipantId) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [selectedParticipantId])

  // Marquer comme lu quand on ouvre une conversation
  useEffect(() => {
    if (!selectedConv || selectedConv.unreadCount < 1 || markConversationAsRead.isPending) return
    markConversationAsRead.mutate({
      conversationId: selectedConv.id,
      userId: currentUserIdStr,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConv?.id, selectedConv?.unreadCount, currentUserIdStr])

  // WebSocket connexion gérée dans App.tsx

  // Join le room WebSocket quand on change de conversation
  useEffect(() => {
    const previousConvId = activeConversationIdRef.current

    // Quitter l'ancien room
    if (previousConvId) {
      websocketClient.leaveConversation(previousConvId)
      activeConversationIdRef.current = null
    }

    // Rejoindre le nouveau room dès qu'on connait l'ID de conversation
    if (selectedConv) {
      websocketClient.joinConversation(selectedConv.id)
      activeConversationIdRef.current = selectedConv.id
    }

    return () => {
      const convId = activeConversationIdRef.current
      if (convId) {
        websocketClient.leaveConversation(convId)
        activeConversationIdRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConv?.id])

  // Écouter les messages WebSocket entrants
  useEffect(() => {
    const handleNewMessage = (data: unknown) => {
      const msg = data as Message
      // Mettre à jour le cache de messages si on est dans cette conversation
      if (msg.senderId === currentUserId || msg.receiverId === currentUserId) {
        const otherId = msg.senderId === currentUserId ? msg.receiverId : msg.senderId
        const key = msgKeys.conversation(currentUserIdStr, String(otherId))
        queryClient.setQueryData<Message[]>(key, old => {
          if (!old) return [msg]
          if (old.some(m => m.id === msg.id)) return old
          return [...old, msg]
        })
        // Rafraîchir la liste des conversations
        void queryClient.invalidateQueries({ queryKey: msgKeys.conversations(currentUserIdStr) })
        void queryClient.invalidateQueries({ queryKey: msgKeys.unread(currentUserIdStr) })

        // Toast seulement si c'est un message reçu ET qu'on n'est pas dans cette conversation
        if (msg.receiverId === currentUserId && selectedParticipantId !== msg.senderId) {
          const senderName = `${msg.sender.firstName} ${msg.sender.lastName}`.trim()
          toast(`💬 ${senderName}: ${msg.content.slice(0, 60)}`, { duration: 4000 })
        }
      }
    }

    const handleUpdatedMessage = (data: unknown) => {
      const msg = data as Message
      const otherId = msg.senderId === currentUserId ? msg.receiverId : msg.senderId
      const key = msgKeys.conversation(currentUserIdStr, String(otherId))
      queryClient.setQueryData<Message[]>(key, old =>
        old?.map(m => (m.id === msg.id ? { ...m, ...msg } : m))
      )
    }

    websocketClient.on('message:new', handleNewMessage)
    websocketClient.on('message:updated', handleUpdatedMessage)
    return () => {
      websocketClient.off('message:new', handleNewMessage)
      websocketClient.off('message:updated', handleUpdatedMessage)
    }
  }, [currentUserId, currentUserIdStr, selectedParticipantId, queryClient])

  const filteredConversations = (conversations ?? []).filter(conv => {
    const name = `${conv.participant.firstName} ${conv.participant.lastName}`.toLowerCase()
    return name.includes(searchQuery.toLowerCase())
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { toast.error('Le fichier dépasse 50 Mo'); return }
    setUploadingFile(true)
    try {
      const reader = new FileReader()
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const base64 = dataUrl.split(',')[1] ?? dataUrl
      const res = await filesService.create({
        name: file.name,
        type: 'document',
        mimeType: file.type || undefined,
        data: base64,
        size: file.size,
      })
      if (res.success && res.data) {
        setPendingAttachment({
          name: file.name,
          fileId: res.data.id,
          mimeType: file.type || res.data.mimeType || undefined,
        })
        toast.success('Fichier prêt à envoyer')
      }
    } catch {
      toast.error("Erreur lors de l'upload du fichier")
    } finally {
      setUploadingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSend = useCallback(async () => {
    const hasText = messageInput.trim().length > 0
    const hasAttachment = Boolean(pendingAttachment)
    if ((!hasText && !hasAttachment) || !selectedParticipantId) return
    const content = hasText
      ? messageInput.trim()
      : `[Fichier: ${pendingAttachment!.name}]`
    const attachment = pendingAttachment
    setMessageInput('')
    setPendingAttachment(null)
    try {
      await createMessage.mutateAsync({
        content: attachment
          ? `${content}${hasText ? `\n[Fichier: ${attachment.name}]` : ''}`.trim()
          : content,
        receiverId: selectedParticipantId,
        ...(attachment
          ? {
              attachmentUrl: `file:${attachment.fileId}`,
              ...(attachment.mimeType ? { attachmentType: attachment.mimeType } : {}),
            }
          : {}),
      })
    } catch {
      setMessageInput(content)
    }
  }, [messageInput, pendingAttachment, selectedParticipantId, createMessage])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const handleDeleteMessage = async (id: number) => {
    if (!window.confirm('Supprimer ce message ?')) return
    setMessageMenuOpen(null)
    await deleteMessage.mutateAsync(id)
  }

  const handleSaveEdit = async (id: number) => {
    if (!editingContent.trim()) { setEditingMessageId(null); return }
    await updateMessage.mutateAsync({ id, data: { content: editingContent.trim() } })
    setEditingMessageId(null)
    setEditingContent('')
  }

  const startConversation = (nurseId: number) => {
    setIsNewConvOpen(false)
    setSelectedParticipantId(nurseId)
  }

  if (!currentUserId) {
    return <div className='flex items-center justify-center h-full'><LoadingSpinner /></div>
  }

  return (
    <div className='flex h-full overflow-hidden bg-gray-50 dark:bg-dark-200'>
      {/* Panneau gauche : liste des conversations */}
      <div className='w-80 flex-shrink-0 flex flex-col bg-white dark:bg-dark-50 border-r border-gray-200 dark:border-gray-800'>
        <div className='p-4 border-b border-gray-200 dark:border-gray-800'>
          <div className='flex items-center justify-between mb-3'>
            <div>
              <h1 className='text-lg font-bold text-gray-900 dark:text-gray-100'>Messages</h1>
              {unreadCount != null && unreadCount > 0 && (
                <p className='text-xs text-primary-500 font-medium'>{unreadCount} non lu{unreadCount > 1 ? 's' : ''}</p>
              )}
            </div>
            <button
              onClick={() => setIsNewConvOpen(true)}
              className='p-2 bg-primary-500/10 hover:bg-primary-500/20 text-primary-500 rounded-xl transition-colors'
              title='Nouvelle conversation'
            >
              <IoAdd className='text-lg' />
            </button>
          </div>
          <div className='relative'>
            <IoSearchOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm' />
            <input
              type='text'
              placeholder='Rechercher...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-dark-100 border border-transparent focus:border-primary-500/50 focus:bg-white dark:focus:bg-dark-50 rounded-xl outline-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400'
            />
          </div>
        </div>

        <div className='flex-1 overflow-y-auto'>
          {conversationsLoading ? (
            <div className='flex justify-center py-8'><LoadingSpinner /></div>
          ) : filteredConversations.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 px-4 text-center'>
              <IoChatbubbleEllipsesOutline className='text-4xl text-gray-300 dark:text-gray-600 mb-3' />
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                {searchQuery ? 'Aucun résultat' : 'Aucune conversation'}
              </p>
              {!searchQuery && (
                <button onClick={() => setIsNewConvOpen(true)} className='mt-3 text-xs text-primary-500 hover:underline'>
                  Démarrer une conversation
                </button>
              )}
            </div>
          ) : (
            filteredConversations.map(conv => {
              const participantName = `${conv.participant.firstName} ${conv.participant.lastName}`
              const isActive = conv.participant.id === selectedParticipantId
              return (
                <button
                  key={conv.id}
                  onClick={() => setSelectedParticipantId(conv.participant.id)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors border-b border-gray-100 dark:border-gray-800/50 ${
                    isActive ? 'bg-primary-50 dark:bg-primary-500/10 border-l-2 border-l-primary-500' : ''
                  }`}
                >
                  <div className='relative flex-shrink-0'>
                    <div className='w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-600/30 border border-primary-500/30 flex items-center justify-center'>
                      <span className='text-xs font-bold text-primary-500'>{getInitials(participantName)}</span>
                    </div>
                    {conv.unreadCount > 0 && (
                      <span className='absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center'>
                        {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center justify-between gap-1'>
                      <p className={`text-sm font-semibold truncate ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-gray-100'}`}>
                        {participantName}
                      </p>
                      {conv.lastMessage && (
                        <span className='text-[10px] text-gray-400 flex-shrink-0'>
                          {formatConvTime(String(conv.lastMessage.createdAt))}
                        </span>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? 'font-semibold text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                        {conv.lastMessage.senderId === currentUserId ? 'Vous: ' : ''}
                        {conv.lastMessage.content}
                      </p>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>

      {/* Panneau droit : chat */}
      {selectedParticipantId ? (
        <div className='flex-1 flex flex-col min-w-0'>
          {/* En-tête */}
          <div className='flex items-center gap-3 px-6 py-4 bg-white dark:bg-dark-50 border-b border-gray-200 dark:border-gray-800 flex-shrink-0'>
            <div className='w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-600/30 border border-primary-500/30 flex items-center justify-center flex-shrink-0'>
              <span className='text-sm font-bold text-primary-500'>
                {selectedConvName ? getInitials(selectedConvName) : '?'}
              </span>
            </div>
            <div>
              <h2 className='font-semibold text-gray-900 dark:text-gray-100'>
                {selectedConvName || 'Nouvelle conversation'}
              </h2>
            </div>
          </div>

          {/* Zone des messages */}
          <div className='flex-1 overflow-y-auto p-6 space-y-3'>
            {conversationLoading ? (
              <div className='flex justify-center py-12'><LoadingSpinner /></div>
            ) : messages.length === 0 ? (
              <div className='flex flex-col items-center justify-center h-full text-center'>
                <IoChatbubbleEllipsesOutline className='text-5xl text-gray-300 dark:text-gray-600 mb-3' />
                <p className='text-gray-500 dark:text-gray-400 font-medium'>Aucun message</p>
                <p className='text-sm text-gray-400 mt-1'>Commencez la conversation !</p>
              </div>
            ) : (
              messages.map(message => {
                const isOwn = message.senderId === currentUserId
                const isEditing = editingMessageId === message.id
                const isOptimistic = message.id < 0

                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: isOptimistic ? 0.6 : 1, y: 0 }}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
                  >
                    <div className={`relative max-w-[65%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                      {isEditing ? (
                        <div className='w-full bg-white dark:bg-dark-100 rounded-2xl p-3 border-2 border-primary-500 shadow-lg min-w-[240px]'>
                          <textarea
                            value={editingContent}
                            onChange={e => setEditingContent(e.target.value)}
                            className='w-full bg-transparent outline-none resize-none text-sm text-gray-900 dark:text-gray-100 mb-2'
                            rows={3}
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter' && e.ctrlKey) void handleSaveEdit(message.id)
                              if (e.key === 'Escape') { setEditingMessageId(null); setEditingContent('') }
                            }}
                          />
                          <div className='flex gap-2 justify-end'>
                            <button
                              onClick={() => { setEditingMessageId(null); setEditingContent('') }}
                              className='px-3 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors'
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => void handleSaveEdit(message.id)}
                              disabled={!editingContent.trim() || updateMessage.isPending}
                              className='px-3 py-1 text-xs rounded-lg bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 transition-colors'
                            >
                              {updateMessage.isPending ? '...' : 'Enregistrer'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className={`relative px-4 py-2.5 rounded-2xl ${
                            isOwn
                              ? 'bg-primary-500 text-white rounded-br-md'
                              : 'bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md shadow-sm'
                          }`}>
                            <p className='text-sm leading-relaxed whitespace-pre-wrap'>
                              {textWithoutFileMarker(message.content) || (message.attachmentUrl ? '' : message.content)}
                            </p>
                            {message.attachmentUrl && (
                              <div className={`mt-2 flex items-center gap-2 rounded-xl px-3 py-2 ${
                                isOwn ? 'bg-white/15' : 'bg-gray-100 dark:bg-dark-100'
                              }`}>
                                <IoDocumentOutline className='flex-shrink-0 text-base' />
                                <span className='min-w-0 flex-1 truncate text-xs font-medium'>
                                  {filenameFromContent(message.content) ?? 'Pièce jointe'}
                                </span>
                                <button
                                  type='button'
                                  title='Ouvrir'
                                  onClick={e => {
                                    e.stopPropagation()
                                    void (async () => {
                                      try {
                                        await openOrDownloadAttachment(
                                          message.attachmentUrl!,
                                          filenameFromContent(message.content) ?? 'piece-jointe',
                                          message.attachmentType,
                                          'open'
                                        )
                                      } catch {
                                        toast.error("Impossible d'ouvrir le fichier")
                                      }
                                    })()
                                  }}
                                  className={`flex-shrink-0 rounded-lg p-1 ${isOwn ? 'hover:bg-white/20' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                >
                                  <IoOpenOutline />
                                </button>
                                <button
                                  type='button'
                                  title='Télécharger'
                                  onClick={e => {
                                    e.stopPropagation()
                                    void (async () => {
                                      try {
                                        await openOrDownloadAttachment(
                                          message.attachmentUrl!,
                                          filenameFromContent(message.content) ?? 'piece-jointe',
                                          message.attachmentType,
                                          'download'
                                        )
                                      } catch {
                                        toast.error('Impossible de télécharger le fichier')
                                      }
                                    })()
                                  }}
                                  className={`flex-shrink-0 rounded-lg p-1 ${isOwn ? 'hover:bg-white/20' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                >
                                  <IoDownloadOutline />
                                </button>
                              </div>
                            )}
                            <div className={`flex items-center gap-1 mt-1 text-[10px] ${isOwn ? 'justify-end text-white/70' : 'text-gray-400'}`}>
                              <span>{formatMessageTime(String(message.createdAt))}</span>
                              {isOwn && (
                                isOptimistic ? (
                                  <IoCheckmark className='text-xs opacity-40' />
                                ) : message.status === 'read' ? (
                                  <IoCheckmarkDone className='text-xs' />
                                ) : message.status === 'delivered' ? (
                                  <IoCheckmarkDone className='text-xs opacity-60' />
                                ) : (
                                  <IoCheckmark className='text-xs opacity-60' />
                                )
                              )}
                            </div>

                            {isOwn && !isOptimistic && (
                              <button
                                onClick={e => {
                                  e.stopPropagation()
                                  setMessageMenuOpen(messageMenuOpen === message.id ? null : message.id)
                                }}
                                className='absolute -top-2 -left-2 opacity-0 group-hover:opacity-100 bg-white dark:bg-dark-100 border border-gray-200 dark:border-gray-700 rounded-lg p-1 shadow-sm transition-opacity'
                              >
                                <IoEllipsisVertical className='text-xs text-gray-500' />
                              </button>
                            )}
                          </div>

                          <AnimatePresence>
                            {isOwn && messageMenuOpen === message.id && (
                              <>
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  exit={{ opacity: 0, scale: 0.95 }}
                                  className='absolute right-0 top-full mt-1 bg-white dark:bg-dark-50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden z-50 min-w-[140px]'
                                >
                                  <button
                                    onClick={() => {
                                      setEditingMessageId(message.id)
                                      setEditingContent(message.content)
                                      setMessageMenuOpen(null)
                                    }}
                                    className='flex items-center gap-2 px-4 py-2.5 w-full text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors'
                                  >
                                    <IoCreateOutline /> Modifier
                                  </button>
                                  <div className='h-px bg-gray-100 dark:bg-gray-700' />
                                  <button
                                    onClick={() => void handleDeleteMessage(message.id)}
                                    disabled={deleteMessage.isPending}
                                    className='flex items-center gap-2 px-4 py-2.5 w-full text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50'
                                  >
                                    <IoTrashOutline /> Supprimer
                                  </button>
                                </motion.div>
                                <div className='fixed inset-0 z-40' onClick={() => setMessageMenuOpen(null)} />
                              </>
                            )}
                          </AnimatePresence>
                        </>
                      )}
                    </div>
                  </motion.div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Zone de saisie */}
          <div className='px-6 py-4 bg-white dark:bg-dark-50 border-t border-gray-200 dark:border-gray-800 flex-shrink-0'>
            {pendingAttachment && (
              <div className='flex items-center gap-2 mb-2 px-3 py-2 bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30 rounded-xl'>
                <IoDocumentOutline className='text-primary-500 flex-shrink-0' />
                <span className='text-sm text-primary-700 dark:text-primary-400 truncate flex-1'>{pendingAttachment.name}</span>
                <button onClick={() => setPendingAttachment(null)} className='text-gray-400 hover:text-red-500 transition-colors flex-shrink-0'>
                  <IoCloseOutline />
                </button>
              </div>
            )}
            <div className='flex items-center gap-3'>
              <input ref={fileInputRef} type='file' className='hidden' onChange={e => void handleFileSelect(e)} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className='flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-dark-100 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 text-gray-500 dark:text-gray-400 rounded-xl flex items-center justify-center transition-colors'
                title='Joindre un fichier (max 50 Mo)'
              >
                {uploadingFile ? <LoadingSpinner size='sm' /> : <IoAttachOutline className='text-lg' />}
              </button>
              <input
                ref={inputRef}
                type='text'
                placeholder='Tapez votre message...'
                value={messageInput}
                onChange={e => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className='flex-1 px-4 py-2.5 bg-gray-100 dark:bg-dark-100 border border-transparent focus:border-primary-500/50 focus:bg-white dark:focus:bg-dark-50 rounded-xl outline-none transition-all text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400'
              />
              <button
                onClick={() => void handleSend()}
                disabled={(!messageInput.trim() && !pendingAttachment) || createMessage.isPending}
                className='flex-shrink-0 w-10 h-10 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl flex items-center justify-center transition-colors'
              >
                {createMessage.isPending ? <LoadingSpinner size='sm' /> : <IoSend className='text-sm' />}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className='flex-1 flex flex-col items-center justify-center text-center p-8'>
          <div className='w-20 h-20 bg-primary-500/10 rounded-2xl flex items-center justify-center mb-4'>
            <IoChatbubbleEllipsesOutline className='text-4xl text-primary-500' />
          </div>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2'>Vos messages</h2>
          <p className='text-gray-500 dark:text-gray-400 text-sm mb-6 max-w-xs'>
            Sélectionnez une conversation ou démarrez-en une nouvelle
          </p>
          <button onClick={() => setIsNewConvOpen(true)} className='btn-primary flex items-center gap-2 px-5 py-2.5'>
            <IoAdd /> Nouvelle conversation
          </button>
        </div>
      )}

      {/* Modal nouvelle conversation */}
      <AnimatePresence>
        {isNewConvOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4'
            onClick={() => setIsNewConvOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col shadow-2xl'
            >
              <div className='flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800'>
                <h2 className='font-bold text-gray-900 dark:text-gray-100'>Nouvelle conversation</h2>
                <button onClick={() => setIsNewConvOpen(false)} className='p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors'>
                  <IoCloseOutline className='text-xl text-gray-500' />
                </button>
              </div>
              <div className='flex-1 overflow-y-auto p-4'>
                <p className='text-xs text-gray-500 dark:text-gray-400 mb-3 px-1'>
                  Sélectionnez un infirmier pour démarrer une conversation
                </p>
                {nursesLoading ? (
                  <div className='flex justify-center py-8'><LoadingSpinner /></div>
                ) : nursesList.filter(n => n.id !== currentUserId).length === 0 ? (
                  <p className='text-center text-gray-400 text-sm py-6'>Aucun autre infirmier</p>
                ) : (
                  <div className='space-y-2'>
                    {nursesList
                      .filter(nurse => nurse.id !== currentUserId)
                      .map(nurse => {
                        const nurseName = [nurse.firstName, nurse.lastName].filter(Boolean).join(' ') || nurse.email
                        const hasExisting = conversations?.some(c => c.participant.id === nurse.id)
                        return (
                          <button
                            key={nurse.id}
                            onClick={() => startConversation(nurse.id)}
                            className='w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-100 hover:bg-primary-50 dark:hover:bg-primary-500/10 border border-gray-200 dark:border-gray-700 hover:border-primary-500/30 transition-all text-left'
                          >
                            <div className='w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0'>
                              <IoPersonOutline className='text-xl text-primary-500' />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <p className='font-semibold text-sm text-gray-900 dark:text-gray-100 truncate'>{nurseName}</p>
                              <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>{nurse.email}</p>
                              {hasExisting && <p className='text-xs text-primary-500 mt-0.5'>Conversation existante</p>}
                            </div>
                          </button>
                        )
                      })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
